import { cn } from "@/lib/utils";
import { ItemHistoryAvatars } from "./ItemHistoryAvatars";
import { ItemHistoryChangeValues } from "./ItemHistoryChangeValues";
import { ItemHistoryDetailPopover } from "./ItemHistoryDetailPopover";
import { PriceModelInputReasoning } from "./PriceModelInputReasoning";
import type { ItemFieldTarget, ItemHistoryActor } from "./itemHistoryLabels";
import {
  entryLabel,
  type ItemHistoryDetail,
  type ItemHistoryEntryLine,
  type ItemHistorySentence,
} from "./itemHistoryRows";

const CHIP_CLASSES =
  "-mx-1 inline-flex rounded-sm px-1 text-left font-semibold hover:bg-muted focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted";

interface ItemHistoryLineProps {
  actor: ItemHistoryActor;
  sentence: ItemHistorySentence;
  time: string | null;
  reason: string | null;
  onSelectField: (target: ItemFieldTarget) => void;
  describeRemoval?: (name: string) => string;
}

/**
 * One write as one line: who, what they did, why when they said so, and when.
 * A changed value sits in its own right-aligned column; a line naming one
 * field jumps the form to it.
 * @param actor - Who made the write
 * @param sentence - What the write did
 * @param time - When the write happened; null for unsaved edits
 * @param reason - The writer's recorded reason, when any
 * @param onSelectField - Jumps the form to a field or BOM row
 * @param describeRemoval - Builds the note shown under the BOM table when a
 *   removed row is selected
 */
export const ItemHistoryLine = ({
  actor,
  sentence,
  time,
  reason,
  onSelectField,
  describeRemoval,
}: ItemHistoryLineProps) => {
  const selectLine = (line: ItemHistoryDetail) => {
    if (!("target" in line) || !line.target) {
      return;
    }
    const note =
      line.type === "removed" && line.target.tab === "bom" && describeRemoval
        ? describeRemoval(line.name)
        : undefined;
    onSelectField(note ? { ...line.target, note } : line.target);
  };
  const entry = sentence.type === "entry" ? sentence.line : null;
  const change = entry?.type === "changed" ? entry : null;

  // Below 24rem the change drops under the sentence, still right-aligned,
  // so the sentence keeps its width.
  return (
    <div className="@container">
      <div
        className={cn(
          "relative grid grid-cols-[34px_minmax(0,1fr)_auto] items-baseline gap-x-2 rounded-md px-2.5 py-1.5 text-sm leading-6 @[24rem]:grid-cols-[34px_minmax(9rem,1fr)_fit-content(40%)_auto]",
          entry?.target && "hover:bg-accent",
        )}
      >
        <div className="col-start-1 row-start-1 mt-px self-start">
          <ItemHistoryAvatars actor={actor} />
        </div>
        <p
          className={cn(
            "col-start-2 row-start-1 min-w-0 break-words",
            !change && "@[24rem]:col-span-2",
          )}
        >
          <span className="font-medium">{actor.name}</span>{" "}
          <span className="text-muted-foreground">{sentence.verb}</span>{" "}
          <SentenceObject sentence={sentence} onSelectLine={selectLine} />
          {reason ? (
            <>
              {" "}
              <span className="relative z-10 inline-flex align-[-1px]">
                <PriceModelInputReasoning
                  reasoning={reason}
                  ariaLabel="Why this change was made"
                />
              </span>
            </>
          ) : null}
          {actor.behalf ? (
            <>
              {" "}
              <span className="text-muted-foreground">on behalf of</span>{" "}
              <span className="font-medium">{actor.behalf.name}</span>
            </>
          ) : null}
          {actor.context ? (
            <>
              {" "}
              <span className="inline-block max-w-full truncate align-bottom text-muted-foreground">
                · {actor.context}
              </span>
            </>
          ) : null}
        </p>
        {change ? (
          // Above the line's click overlay: a cut value opens its detail, a
          // value that fits jumps like the rest of the line.
          <div className="relative z-10 col-start-2 row-start-2 min-w-0 @[24rem]:col-start-3 @[24rem]:row-start-1 @[24rem]:pl-2">
            <ItemHistoryDetailPopover
              className="w-full rounded-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              details={[change]}
              onSelectLine={selectLine}
              canOpen={isCut}
              onClickClosed={() => selectLine(change)}
            >
              <ItemHistoryChangeValues from={change.from} to={change.to} />
            </ItemHistoryDetailPopover>
          </div>
        ) : null}
        <span className="col-start-3 row-start-1 whitespace-nowrap text-xs tabular-nums text-muted-foreground @[24rem]:col-start-4">
          {time}
        </span>
      </div>
    </div>
  );
};

// What follows the verb: the entry, the item, or the chip.
const SentenceObject = ({
  sentence,
  onSelectLine,
}: {
  sentence: ItemHistorySentence;
  onSelectLine: (line: ItemHistoryDetail) => void;
}) => {
  if (sentence.type === "item") {
    return <span className="text-muted-foreground">the item</span>;
  }
  if (sentence.type === "chip") {
    return (
      <>
        {sentence.lead ? (
          <span className="text-muted-foreground">{sentence.lead} · </span>
        ) : null}
        <ItemHistoryDetailPopover
          className={CHIP_CLASSES}
          details={sentence.details}
          onSelectLine={onSelectLine}
        >
          {sentence.chip}
        </ItemHistoryDetailPopover>
      </>
    );
  }
  const { line, collection } = sentence;
  return (
    <>
      <EntryName line={line} onSelect={() => onSelectLine(line)} />
      {line.type !== "changed" && collection ? (
        <span className="text-muted-foreground">
          {" "}
          {line.type === "added" ? "to" : "from"} {collection}
        </span>
      ) : null}
    </>
  );
};

// The entry's name. When the entry has a field to jump to, the name is the
// button, and its overlay stretches the click over the whole line.
const EntryName = ({
  line,
  onSelect,
}: {
  line: ItemHistoryEntryLine;
  onSelect: () => void;
}) => {
  if (!line.target) {
    return <span className="font-semibold">{entryLabel(line)}</span>;
  }
  return (
    <button
      type="button"
      onClick={onSelect}
      className="text-left font-semibold after:absolute after:inset-0 after:rounded-md focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
    >
      {entryLabel(line)}
    </button>
  );
};

// Whether a value in the cell is cut short by its ellipsis right now.
const isCut = (cell: HTMLElement) =>
  [...cell.querySelectorAll("span")].some(
    (span) => span.scrollWidth > span.clientWidth,
  );
